import React from "react";
import {
  Button,
  Checkbox,
  ShareOutlineIcon,
  AgeGatedPressable,
  Alert,
  AlertBox,
} from "@gonoodle/gn-universe-ui";
import { Dialog } from "@gonoodle/gn-universe-ui/lib/next";
import { Formik, Form } from "formik";
import Joi from "joi";
import { forEach } from "lodash";
import {
  REFERRAL_METHODS,
  REFERRAL_TYPES,
  SECTIONS,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { usePathname, useSearchParams } from "next/navigation";

import { EmailField } from "../FormElements";
import { useTeacherReferralMutation, useRouter } from "../../hooks";
import { useLogEvent } from "../../contexts/Analytics";

const fields = {
  email: "email",
  userAgreement: "userAgreement",
};

export default function TeacherReferral({ isInitialOpen = false }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const alert = Alert.useAlert();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const { logEvent: logReferralMadeEvent } = useLogEvent({
    event: "Referral Made",
    properties: {
      referralMethod: REFERRAL_METHODS.EMAIL,
      referralType: REFERRAL_TYPES.TEACHER,
      sourceElement: SECTIONS_TYPES.PROFILE_MENU,
      sourceName: SECTIONS.REFER_GONOODLE,
    },
    options: {
      includeReferrer: false,
      includeSourcePage: true,
      includeSourcePageType: true,
    },
  });
  const {
    referTeacher,
    isReferTeacherLoading,
    referTeacherError,
  } = useTeacherReferralMutation({
    onSuccess: () => {
      setIsOpen(false);

      alert.publish(
        "Thanks for spreading the word! Your referral means another teacher gets a tool they can trust to engage and energize their classroom—and you’re one step closer to earning exclusive rewards for it.",
      );

      logReferralMadeEvent();
    },
  });

  React.useEffect(() => {
    if (isInitialOpen) {
      setIsOpen(true);
    }
  }, [isInitialOpen]);

  const onTeacherReferralClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
    router.replace(`${pathname}?referral=teacher`);
  };

  return (
    <>
      <AgeGatedPressable
        onPress={onTeacherReferralClick}
        className="flex gap-3 items-center font-semibold text-sm w-full text-left hover:bg-gray-100 rounded p-sm focus:ring focus:ring-purple focus:outline-none"
      >
        <ShareOutlineIcon className="text-purple w-4 h-4" aria-hidden="true" />
        Refer GoNoodle
      </AgeGatedPressable>

      {isOpen && (
        <Dialog.Root
          open={true}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (isOpen && searchParams.get("referral") === "teacher") {
              router.replace(pathname);
            }
          }}
          dismissable={true}
        >
          <Dialog.Trigger>
            <ShareOutlineIcon className="w-6 h-6 text-off-white" />
          </Dialog.Trigger>

          <Dialog.Panel className="space-y-md">
            <Dialog.Title>Love GoNoodle? Share GoNoodle!</Dialog.Title>

            <Dialog.Description className="text-left">
              Know a teacher who could use more movement, focus, and fun in
              their classroom? Share their email and help grow the GoNoodle
              community.
            </Dialog.Description>
            <Dialog.Description className="text-left">
              We&apos;ll send them a special getting-started guide, and NEVER
              share or sell their emails.
            </Dialog.Description>
            <Dialog.Description className="text-left italic">
              P.S. Referring unlocks{" "}
              <a
                href="https://www.teachers.gonoodle.com/clubgonoodle"
                className="cursor-pointer underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                exclusive rewards
              </a>{" "}
              for registered US teachers!
            </Dialog.Description>

            {referTeacherError && <AlertBox text={referTeacherError} />}

            <Formik
              initialValues={{
                [fields.email]: "",
                [fields.userAgreement]: false,
              }}
              validate={(values) => {
                const errors = {};
                const userAgreementSchema = Joi.object({
                  userAgreement: Joi.bool().valid(true).messages({
                    "any.only": "Please confirm the above consent",
                  }),
                });

                const { error } = userAgreementSchema.validate(values, {
                  abortEarly: false,
                  allowUnknown: true,
                  errors: {
                    label: "path",
                  },
                });

                if (error) {
                  forEach(error.details, (errorDetail) => {
                    errors[errorDetail.context.key] = errorDetail.message;
                  });
                }

                return errors;
              }}
              onSubmit={({ email }) => {
                referTeacher({ email });
              }}
            >
              {({ values, touched, errors, handleBlur, setFieldValue }) => (
                <Form noValidate className="space-y-md">
                  <div className="flex flex-col">
                    <EmailField
                      name={fields.email}
                      label="Enter A Valid Email Address for a US Teacher"
                      errorMessage="Please Enter a Valid Email"
                      autoComplete="off"
                      onBlur={handleBlur}
                    />

                    <div className="flex flex-row items-start py-sm">
                      <Checkbox
                        name={fields.userAgreement}
                        required={true}
                        label={
                          <span className="text-gray-900">
                            I confirm that I have consent from the owner of the
                            above email to refer them to GoNoodle.
                          </span>
                        }
                        checked={values[fields.userAgreement]}
                        error={
                          touched[fields.userAgreement] &&
                          errors[fields.userAgreement]
                        }
                        onChange={(val) =>
                          setFieldValue(fields.userAgreement, val)
                        }
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>

                  <Button
                    variant={Button.VARIANTS.dark}
                    size={Button.SIZES.md}
                    className="w-full"
                    type="submit"
                    disabled={isReferTeacherLoading}
                  >
                    Submit Email and Earn Teacher Rewards!
                  </Button>
                </Form>
              )}
            </Formik>
          </Dialog.Panel>
        </Dialog.Root>
      )}
    </>
  );
}
